"use client";

import { DirectionProvider } from "@radix-ui/react-direction";
import { QueryClientProvider } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useLocale } from "next-intl";
import type { ReactNode } from "react";
import {
  BottomNav,
  SideNav,
  SplashScreen,
  TopNav,
  W3MModalDirFix,
} from "@/components/core";
import { ImpersonationBanner } from "@/components/core/ImpersonationBanner";
import { WalletLayer } from "@/components/core/WalletLayer";
import { SecondFactorGate } from "@/components/features/auth/SecondFactorGate/SecondFactorGate";
import { TouchProvider } from "@/components/ui/hybrid-tooltip";
import { Toaster } from "@/components/ui/sonner";
import { useMaintenanceCheck } from "@/hooks/useMaintenanceCheck";
import { usePathname } from "@/i18n/navigation";
import { getDirection, type Locale } from "@/lib/i18n";
import { resolveChrome } from "@/lib/nav-chrome";
import { queryClient } from "@/lib/query-client";
import { cn } from "@/lib/utils";

const DynamicRouteProgressBar = dynamic(
  () =>
    import("@/components/core/RouteProgressBar").then((mod) => ({
      default: mod.RouteProgressBar,
    })),
  { ssr: false },
);

const DynamicVConsole = dynamic(
  () =>
    import("@/components/core/VConsole").then((mod) => ({
      default: mod.VConsole,
    })),
  { ssr: false },
);

interface AppProviderProps {
  children: ReactNode;
  hasBottomNav?: boolean;
  initialPath?: string;
  cookies: string | null;
}

const MaintenanceGuard = ({ children }: { children: ReactNode }) => {
  useMaintenanceCheck();
  return <>{children}</>;
};

export const AppProvider = ({
  children,
  hasBottomNav = true,
  initialPath,
  cookies,
}: AppProviderProps) => {
  const pathname = usePathname();
  const locale = useLocale() as Locale;
  const dir = getDirection(locale);

  // Use server pathname for initial check, then client pathname when available
  const currentPathname = pathname || initialPath;

  const { showTopNav, showBottomNav, showSidebar } =
    resolveChrome(currentPathname);
  const shouldShowBottomNav = hasBottomNav && showBottomNav;

  return (
    <QueryClientProvider client={queryClient}>
      <DirectionProvider dir={dir}>
        <MaintenanceGuard>
          <TouchProvider>
            <WalletLayer cookies={cookies}>
              <SplashScreen />
              <W3MModalDirFix />
              <ImpersonationBanner />
              {showTopNav && <TopNav />}
              <DynamicRouteProgressBar />
              {/* RTL grid: SideNav is the FIRST grid child → inline-start →
                  RIGHT under dir="rtl" (matches the old /profile sidebar).
                  Gated on showSidebar so focus flows stay full-width (no dead
                  240px gutter); SideNav also hides itself below `lg`.

                  The grid is centered (`mx-auto`) and capped (`max-w-screen-2xl`)
                  so the sidebar + content move as ONE unit instead of the
                  sidebar hugging the viewport edge while the content centers
                  itself far away — that split is what left the big empty
                  gutters on wide screens. A small start inset keeps the rail
                  off the edge even before the cap is reached. */}
              <div
                className={cn(
                  showSidebar &&
                    "lg:mx-auto lg:grid lg:max-w-screen-2xl lg:grid-cols-[240px_1fr] lg:gap-x-2 lg:ps-4",
                )}
              >
                {showSidebar && <SideNav initialPath={initialPath} />}
                <div className="min-w-0">{children}</div>
              </div>
              {shouldShowBottomNav && <BottomNav initialPath={initialPath} />}
              <SecondFactorGate />
              <Toaster />
              {process.env.NEXT_PUBLIC_VCONSOLE === "true" && (
                <DynamicVConsole />
              )}
            </WalletLayer>
          </TouchProvider>
        </MaintenanceGuard>
      </DirectionProvider>
    </QueryClientProvider>
  );
};
